import api from '@/services/api';

export type UnifiedAuditScope = 'tenant' | 'platform' | 'pe_portfolio';

export type UnifiedAuditLogDto = {
  id: string;
  tenant_id: string | null;
  tenant_name: string | null;
  user_id: string | null;
  user_email: string | null;
  access_control_type: string;
  decision: string;
  resource_type: string;
  resource_id: string | null;
  action: string;
  message: string | null;
  ip_address: string | null;
  metadata: unknown;
  created_at: string;
};

export type UnifiedAuditMeta = {
  scope: UnifiedAuditScope;
  tenantOptions: { id: string; name: string }[];
  resourceTypes: string[];
  actions: string[];
};

export type UnifiedAuditTrendSeries = {
  key: string;
  label: string;
  counts: number[];
};

export type UnifiedAuditTrends = {
  bucket: 'day';
  labels: string[];
  byTenant: UnifiedAuditTrendSeries[];
  byResourceType: UnifiedAuditTrendSeries[];
};

export async function fetchUnifiedAuditMeta(): Promise<UnifiedAuditMeta> {
  const res = await api.get<{ success: boolean; data: UnifiedAuditMeta }>('/unified-audit-logs/meta');
  return res.data.data;
}

export type AuditUserOption = { id: string; email: string; displayName: string | null };

/** Typeahead for users (name/email), scoped like audit logs. Optional tenant narrows search (platform / PE). */
export async function searchUnifiedAuditUsers(params: {
  q: string;
  tenantId?: string | null;
}): Promise<AuditUserOption[]> {
  const q = params.q.trim();
  if (q.length < 2) return [];
  const query: Record<string, string> = { q };
  if (params.tenantId && params.tenantId.trim().length > 0) {
    query.tenantId = params.tenantId.trim();
  }
  const res = await api.get<{
    success: boolean;
    data: { users: AuditUserOption[] };
  }>('/unified-audit-logs/users', { params: query });
  return res.data.data.users;
}

export async function fetchUnifiedAuditTrends(params: {
  from: Date;
  to: Date;
  resourceType?: string;
  action?: string;
  tenantId?: string | null;
  userId?: string | null;
}): Promise<UnifiedAuditTrends> {
  const q: Record<string, string> = {
    from: params.from.toISOString(),
    to: params.to.toISOString(),
  };
  if (params.resourceType && params.resourceType.trim().length > 0) {
    q.resourceType = params.resourceType.trim();
  }
  if (params.action && params.action.trim().length > 0) {
    q.action = params.action.trim();
  }
  if (params.tenantId && params.tenantId.trim().length > 0) {
    q.tenantId = params.tenantId.trim();
  }
  if (params.userId && params.userId.trim().length > 0) {
    q.userId = params.userId.trim();
  }
  const res = await api.get<{ success: boolean; data: UnifiedAuditTrends }>('/unified-audit-logs/trends', {
    params: q,
  });
  return res.data.data;
}

export async function fetchUnifiedAuditLogs(params: {
  from: Date;
  to: Date;
  page?: number;
  limit?: number;
  resourceType?: string;
  action?: string;
  tenantId?: string | null;
  userId?: string | null;
}): Promise<{ logs: UnifiedAuditLogDto[]; pagination: { page: number; limit: number; total: number; pages: number } }> {
  const q: Record<string, string | number> = {
    from: params.from.toISOString(),
    to: params.to.toISOString(),
    page: params.page ?? 1,
    limit: params.limit ?? 50,
  };
  if (params.resourceType && params.resourceType.trim().length > 0) {
    q.resourceType = params.resourceType.trim();
  }
  if (params.action && params.action.trim().length > 0) {
    q.action = params.action.trim();
  }
  if (params.tenantId && params.tenantId.trim().length > 0) {
    q.tenantId = params.tenantId.trim();
  }
  if (params.userId && params.userId.trim().length > 0) {
    q.userId = params.userId.trim();
  }
  const res = await api.get<{
    success: boolean;
    data: {
      logs: UnifiedAuditLogDto[];
      pagination: { page: number; limit: number; total: number; pages: number };
    };
  }>('/unified-audit-logs', { params: q });
  return res.data.data;
}
